import React, { useContext, useMemo } from 'react'
import PropTypes from 'prop-types'
import Table from 'react-bootstrap/Table'
import { twoDigitMoney, titleize } from 'helpers/utils'
import UserContext from 'context/user-context'
import CenteredSpinner from 'shared/centered-spinner'
import TableError from 'shared/tables/table-error'
import { useContractAccounts } from 'api/hooks/use-transactions'

// A contract's accounts grouped by type (Balance/Expense/Revenue), with amounts.
function ContractAccounts({ transactionId, sectionName, active }) {
  const { municipality, municipality_sections } = useContext(UserContext)
  const { data, isLoading, isError } = useContractAccounts({
    id: transactionId,
    options: { enabled: active }
  })

  const currentSection = useMemo(() => {
    if (!municipality_sections || !sectionName) return null
    return municipality_sections.find((ms) => ms.section?.name === sectionName)
  }, [municipality_sections, sectionName])

  const isEnterpriseERP = useMemo(
    () => currentSection?.system?.name === 'Enterprise ERP',
    [currentSection]
  )

  const getSegmentName = (segmentNumber) => {
    const segmentKey = `seg_${segmentNumber}_name`
    return municipality?.setting?.[segmentKey] || `Segment ${titleize(segmentNumber)}`
  }

  const renderSegmentCell = (account, segmentNumber) => {
    const name = account?.[`seg_${segmentNumber}_name`]
    const code = account?.[`seg_${segmentNumber}_code`]
    if (!name && !code) return <td key={segmentNumber} />
    return (
      <td key={segmentNumber}>{name && code ? `${name} (${code})` : name || code || ''}</td>
    )
  }

  const segmentColumns = isEnterpriseERP
    ? ['Org', 'Obj']
    : ['one', 'two', 'three', 'four', 'five'].map(getSegmentName)
  const totalColumns = segmentColumns.length + 3

  const renderSegmentCells = (entry) => {
    if (isEnterpriseERP) {
      return (
        <>
          <td>{entry.org?.name} ({entry.org?.code})</td>
          <td>{entry.object?.name} ({entry.object?.code})</td>
        </>
      )
    }
    return ['one', 'two', 'three', 'four', 'five'].map((n) =>
      renderSegmentCell(entry.account, n)
    )
  }

  if (isLoading) return <CenteredSpinner />

  const groups = data?.groups || []

  return (
    <div className="w-100">
      <Table className="border border-light" size="sm">
        <thead className="border-bottom border-light">
          <tr>
            {segmentColumns.map((label) => (
              <th key={label}>{label}</th>
            ))}
            <th>Account</th>
            <th>Description</th>
            <th>Amount</th>
            <th>Lines</th>
          </tr>
        </thead>
        <tbody>
          {isError && <TableError columns={totalColumns} />}
          {!isError && groups.length === 0 && (
            <tr>
              <td colSpan={totalColumns} className="text-center text-muted py-4">
                No accounts are tied to this contract.
              </td>
            </tr>
          )}
          {groups.map((group) => (
            <React.Fragment key={group.account_type}>
              <tr className="bg-light">
                <th colSpan={totalColumns} className="text-primary">
                  {group.account_type_name}
                </th>
              </tr>
              {group.accounts.map((entry) => (
                <tr key={entry.account.id}>
                  {renderSegmentCells(entry)}
                  <td>{entry.account.full_account}</td>
                  <td>{entry.account.account_description}</td>
                  <td>{twoDigitMoney(entry.total_amount)}</td>
                  <td>{entry.line_count}</td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </Table>
    </div>
  )
}

ContractAccounts.propTypes = {
  transactionId: PropTypes.string,
  sectionName: PropTypes.string,
  active: PropTypes.bool
}

export default ContractAccounts
